/**
 * WHERE "SCORE" GOES — one answer for every button that promises it.
 *
 * The bottom bar's Scoring tab, Home's main button mid-round and the end of
 * the quick-round wizard all mean "take me to my card". Home used to send its
 * "Resume Scoring" to the LEADERBOARD instead, which is how the same intent
 * reached two different screens. Lifted from BottomNav unchanged, so the bar's
 * routing tests cover it.
 *
 * Returns `{ route, state }` for `navigate(route, { state })`.
 */
export const enterScoresRouteFor = (playerStatus) => {
  // Where Scoring goes during a live round. With no round on, Play goes to
  // Play Golf — it used to go to a "no open rounds" page, which is where
  // the old Enter Scores tab sent everyone six days a week.
  let route = '/play';
  let state = {};

  if (playerStatus.status === 'active_round' && playerStatus.is_team_round) {
    // Shared-ball team rounds (Foursomes/Greensomes/Texas Scramble) route to
    // the same team-aware Enter Scores page; it sends a member straight to
    // their own team's card and shows organisers a team-select list.
    route = `/tournaments/${playerStatus.tournament_id}/scorecard-input`;
    state = { courseSequence: playerStatus.round_number || 1 };
  } else if (playerStatus.status === 'active_round') {
    // STRAIGHT TO THE CARD WHERE THE PICKER IS NOT A CHOICE.
    //
    // The picker costs a screen and, measured on a one-bar link, seconds:
    // it asks the server which groups there are before it can send anyone
    // anywhere. But `/me/status` has already been answered by the time this
    // bar renders, and it now carries the caller's own tee-sheet row — so
    // the common case needs no request at all.
    //
    // THE PICKER STAYS WHERE THERE IS SOMETHING TO PICK. An organiser may
    // score any group, so with more than one group out they are being
    // offered a genuine choice and must keep it. Everyone else only ever
    // had their own card. This is the same test the server applies in
    // `/tournaments/<id>/scoring_groups` before deciding whether to send
    // one group or all of them, which is why it can be made here without
    // the two drifting apart.
    //
    // It is a SHORTCUT, NOT A GRANT. The route can only name the caller's
    // own group, and the card authorises itself on arrival — a wrong guess
    // costs a redirect, never an unauthorised edit.
    const sc = playerStatus.scoring;
    const ownCard = sc && sc.group_number != null && sc.course_id != null;
    const mustChoose = sc && sc.can_enter_any && (sc.group_count || 0) > 1;

    if (ownCard && !mustChoose) {
      const tee = sc.tee_id ? `-${sc.tee_id}` : '';
      route = `/live-scoring/${playerStatus.tournament_id}/`
        + `${sc.round_number}-${sc.group_number}-${sc.course_id}${tee}`;
    } else {
      // The round matters here for the same reason it does above: without
      // it the picker has no round in context and lists every started
      // group in the tournament, so a two-round day offers Round 1 and
      // Round 2 side by side with only the small print to tell them
      // apart.
      //
      // But it is only sent when it is actually KNOWN. Defaulting a
      // missing round to 1 is worse than sending nothing: the picker's
      // query drops finalised rounds, so on a two-round day whose first
      // round is already in the books, asking for round 1 returns an
      // empty list — "No open rounds for scoring" on a day the player is
      // out on the course. With no round at all the picker falls back to
      // listing every started group, which is at least always answerable.
      route = `/tournaments/${playerStatus.tournament_id}/scorecard-input`;
      if (playerStatus.round_number != null) {
        state = { courseSequence: playerStatus.round_number };
      }
    }
  }
  return { route, state };
};
